// AnimalAddendumAnimalCard — one animal on the household "tell us about your new animal" form
// (WEL-153), split out of the page so it renders without the network chain.
//
// RRA v2.2 part B (WEL-180): a COVERED animal (the server prices it at no additional animal rent or
// deposit) is pre-listed with what the server knows prefilled — a name or type it gave is fixed — and
// cannot be removed. The household completes the rest. The card says nothing about money or why (Rule H):
// the "{Name} — no additional animal rent or deposit." line belongs to the page's terms block.
//
//   cd apps/web && npx tsx --test src/components/AnimalAddendumAnimalCard.test.ts

import { useRef } from 'react';
import { AnimalQuestionFields } from '@/components/AnimalQuestionFields';
import { asksAddendumPhoto, type AnimalAddendumDraftAnimal } from '@/lib/animalAddendumForm';

export function AnimalAddendumAnimalCard(props: {
  animal: AnimalAddendumDraftAnimal;
  index: number;
  total: number;
  disabled: boolean;
  uploading: boolean;
  photoError: string | null;
  onUpdate: (id: string, patch: Partial<AnimalAddendumDraftAnimal>) => void;
  onRemove: (id: string) => void;
  onPickPhoto: (id: string, file: File) => void;
  onClearPhoto: (id: string) => void;
}): React.JSX.Element {
  const { animal: a, index, total, disabled, uploading, photoError } = props;
  const fileRef = useRef<HTMLInputElement>(null);
  const heading = a.name.trim() || (total > 1 ? `Animal ${index + 1}` : 'Your new animal');

  return (
    <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-5)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <h3
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'var(--text-md)',
            fontWeight: 400,
            color: 'var(--fg)',
            margin: 0,
          }}
        >
          {heading}
        </h3>
        {total > 1 && a.covered === undefined ? (
          <button
            type="button"
            className="btn"
            data-variant="danger-ghost"
            data-size="sm"
            disabled={disabled}
            onClick={() => props.onRemove(a.id)}
          >
            Remove
          </button>
        ) : null}
      </div>

      <AnimalQuestionFields
        idPrefix={`aap-${index}`}
        value={a}
        disabled={disabled}
        {...(a.covered ? { locked: { name: a.covered.lockName, kind: a.covered.lockKind } } : {})}
        onChange={(patch) => props.onUpdate(a.id, patch)}
        licenseHelper="If you don't have it yet, leave it blank and sort it out with the city when you can."
      />

      {/* Photo — optional, uploads the moment it is picked so a slow connection
          never holds up the submit. A failure is said plainly and moves on.
          Offered only for the kinds the matrix asks a photo of (a dog, a cat, a
          bird); the addendum asks the household to email one for exactly those. */}
      {asksAddendumPhoto(a) ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            marginTop: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          {a.photo?.previewUrl ? (
            <img
              src={a.photo.previewUrl}
              alt={`${a.name.trim() || 'Your animal'}`}
              style={{
                width: 88,
                height: 88,
                objectFit: 'cover',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
              }}
            />
          ) : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Clear the input so re-picking the SAME file fires change again.
                e.target.value = '';
                if (file) props.onPickPhoto(a.id, file);
              }}
            />
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                disabled={disabled || uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading
                  ? 'Adding photo…'
                  : a.photo
                    ? 'Choose a different photo'
                    : 'Add a photo (optional)'}
              </button>
              {a.photo ? (
                <button
                  type="button"
                  className="btn"
                  data-variant="ghost"
                  data-size="sm"
                  disabled={disabled || uploading}
                  onClick={() => props.onClearPhoto(a.id)}
                >
                  Remove
                </button>
              ) : null}
            </div>
            {photoError ? (
              <span className="helper" data-state="error">
                {photoError}
              </span>
            ) : !a.photo ? (
              <span className="helper">A phone snapshot is perfect.</span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

